/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  HttpStatusCode,
  TalerError,
  TalerMerchantApi,
  assertUnreachable
} from "@gnu-taler/taler-util";
import {
  LocalNotificationBannerBulma,
  useTranslationContext
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { ErrorLoadingMerchant } from "../../../../components/ErrorLoadingMerchant.js";
import { Loading } from "../../../../components/exception/loading.js";
import { useSessionContext } from "../../../../context/session.js";
import { WithId } from "../../../../declaration.js";
import { useOtpDeviceDetails } from "../../../../hooks/otp.js";
import { LoginPage } from "../../../login/index.js";
import { NotFoundPageOrAdminCreate } from "../../../notfound/index.js";
import { CreatedSuccessfully } from "../create/CreatedSuccessfully.js";
import { UpdatePage } from "./UpdatePage.js";

export type Entity = TalerMerchantApi.OtpDevicePatchDetails & WithId;

interface Props {
  onBack?: () => void;
  onConfirm: () => void;
  vid: string;
}
export default function UpdateValidator({
  vid,
  onConfirm,
  onBack,
}: Props): VNode {
  const result = useOtpDeviceDetails(vid);

  const [keyUpdated, setKeyUpdated] =
    useState<TalerMerchantApi.OtpDeviceAddDetails | null>(null);

  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return <ErrorLoadingMerchant error={result} />;
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.NotFound: {
        return <NotFoundPageOrAdminCreate />;
      }
      case HttpStatusCode.Unauthorized: {
        return <LoginPage focus />
      }
      default: {
        assertUnreachable(result);
      }
    }
  }

  if (keyUpdated) {
    return <CreatedSuccessfully entity={keyUpdated} onConfirm={onConfirm} />;
  }

  return (
    <Fragment>
      <UpdatePage
        device={{
          id: vid,
          otp_algorithm: result.body.otp_algorithm,
          otp_device_description: result.body.device_description,
          otp_key: "",
          otp_ctr: result.body.otp_ctr,
        }}
        onBack={onBack}
        onUpdated={(d) => {
          setKeyUpdated({
            otp_algorithm: d.otp_algorithm,
            otp_device_description: d.otp_device_description,
            otp_device_id: d.id,
            otp_key: d.otp_key,
            otp_ctr: d.otp_ctr
          })
        }}
      />
    </Fragment>
  );
}
